import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { comparerVersions, PLATEFORMES_APP } from '@artosera/shared';
import type { ExigencesPlateforme, PlateformeApp } from '@artosera/shared';
import { Badge, Button, Dialog, Input } from '@artosera/ui';
import { ApiError, modifierVersionsApp, obtenirVersionsApps } from '../lib/api.js';
import {
  brouillonDe,
  erreursDe,
  estModifie,
  estRevisionPerimee,
  releveLaMinimale,
  reglageDe,
  type BrouillonVersions,
  type ChampVersions,
} from '../lib/versionsApps.js';
import { usePeut } from '../auth/usePeut.js';
import type { VersionsAppsVue } from '../types.js';
import pageStyles from '../styles/page.module.css';
import formStyles from '../styles/form.module.css';
import styles from './AppsMobiles.module.css';

const CLE_VERSIONS = ['console', 'apps-versions'] as const;
const CHAMPS_VERSION: readonly ChampVersions[] = ['minimale', 'conseillee'];

/**
 * Apps mobiles : la version minimale (sous elle, l'app est bloquée) et la
 * version conseillée (sous elle, un bandeau) de chaque plateforme. Relever la
 * minimale est un geste de mise en production : une confirmation le dit avant
 * d'enregistrer. Écrire exige `console:manage-app-versions` (exploitation,
 * direction) ; les autres rôles de la console lisent.
 */
export function AppsMobiles(): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const ecriture = peut('console:manage-app-versions');
  const requete = useQuery({ queryKey: CLE_VERSIONS, queryFn: obtenirVersionsApps });

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('appsMobiles.titre')}</h1>
          <p>{t('appsMobiles.texte')}</p>
        </div>
      </div>

      {!ecriture ? (
        <p className={`${pageStyles.banniere} ${styles.lectureSeule}`}>
          {t('appsMobiles.lectureSeule')}
        </p>
      ) : null}

      {requete.isLoading ? (
        <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>
      ) : requete.isError || !requete.data ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {t('appsMobiles.erreurChargement')}
        </p>
      ) : (
        <div className={styles.cartes}>
          {PLATEFORMES_APP.map((plateforme) => (
            <CarteVersions
              key={plateforme}
              plateforme={plateforme}
              exigence={requete.data.exigences[plateforme]}
              revision={requete.data.revision}
              ecriture={ecriture}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface CarteProps {
  plateforme: PlateformeApp;
  exigence: ExigencesPlateforme;
  revision: number;
  ecriture: boolean;
}

function CarteVersions({ plateforme, exigence, revision, ecriture }: CarteProps): JSX.Element {
  const { t } = useTranslation('console');
  const queryClient = useQueryClient();
  const nom = t(`appsMobiles.plateformes.${plateforme}`);

  // Le brouillon n'existe que pendant une saisie : sinon, la valeur relue fait foi.
  const [saisie, setSaisie] = useState<BrouillonVersions | null>(null);
  const [tente, setTente] = useState(false);
  const [confirme, setConfirme] = useState(false);
  const [succes, setSucces] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const brouillon = saisie ?? brouillonDe(exigence);
  const erreurs = erreursDe(brouillon);
  const modifie = estModifie(brouillon, exigence);
  const leve = releveLaMinimale(brouillon, exigence);

  const mutation = useMutation({
    mutationFn: () => modifierVersionsApp(plateforme, reglageDe(brouillon), revision),
    onMutate: () => {
      setErreur(null);
      setSucces(false);
    },
    onSuccess: (reponse: VersionsAppsVue) => {
      queryClient.setQueryData(CLE_VERSIONS, reponse);
      setSaisie(null);
      setTente(false);
      setConfirme(false);
      setSucces(true);
    },
    onError: async (cause) => {
      setConfirme(false);
      if (estRevisionPerimee(cause)) {
        // L'écran ne garde pas ce que le serveur a démenti : on relit et on repart de sa valeur.
        setSaisie(null);
        setErreur(t('appsMobiles.conflit'));
        await queryClient.invalidateQueries({ queryKey: CLE_VERSIONS });
      } else if (cause instanceof ApiError && cause.status === 503) {
        setErreur(t('appsMobiles.journalIndisponible'));
      } else {
        setErreur(t('appsMobiles.erreurGenerique'));
      }
    },
  });

  const changer = (champ: ChampVersions, valeur: string): void => {
    setSaisie({ ...brouillon, [champ]: valeur });
    setSucces(false);
  };

  const envoyer = (event: FormEvent): void => {
    event.preventDefault();
    setTente(true);
    if (Object.keys(erreurs).length > 0 || !modifie) return;
    if (leve) setConfirme(true);
    else mutation.mutate();
  };

  const id = (champ: ChampVersions): string => `apps-${plateforme}-${champ}`;
  const lecture = !ecriture;

  return (
    <section
      className={`${pageStyles.panneau} ${styles.carte}`}
      aria-labelledby={`apps-${plateforme}-titre`}
      data-plateforme={plateforme}
    >
      <header className={styles.enteteCarte}>
        <h2 id={`apps-${plateforme}-titre`}>{nom}</h2>
        <Badge tone={exigence.minimale ? 'reserved' : 'withdrawn'}>
          {exigence.minimale
            ? t('appsMobiles.etat.minimale', { version: exigence.minimale })
            : t('appsMobiles.etat.aucune')}
        </Badge>
      </header>

      <Effet
        plateforme={plateforme}
        reglage={reglageDe(brouillon)}
        valide={Object.keys(erreurs).length === 0}
      />

      <form onSubmit={envoyer} noValidate>
        <div className={styles.champs}>
          {CHAMPS_VERSION.map((champ) => (
            <Champ
              key={champ}
              id={id(champ)}
              champ={champ}
              valeur={brouillon[champ]}
              erreur={tente ? erreurs[champ] : undefined}
              lecture={lecture}
              mono
              placeholder={t('appsMobiles.exemples.version')}
              onChange={(valeur) => changer(champ, valeur)}
            />
          ))}
          <Champ
            id={id('urlBoutique')}
            champ="urlBoutique"
            valeur={brouillon.urlBoutique}
            erreur={tente ? erreurs.urlBoutique : undefined}
            lecture={lecture}
            placeholder={t('appsMobiles.exemples.url')}
            onChange={(valeur) => changer('urlBoutique', valeur)}
            large
          />
        </div>

        <div role="status" aria-live="polite" aria-atomic="true">
          {succes ? (
            <p className={`${pageStyles.banniere} ${styles.succes}`}>
              {t('appsMobiles.succes', { plateforme: nom })}
            </p>
          ) : null}
        </div>
        {erreur ? (
          <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
            {erreur}
          </p>
        ) : null}

        {ecriture ? (
          <div className={styles.pied}>
            <Button type="submit" disabled={!modifie} loading={mutation.isPending && !confirme}>
              {mutation.isPending && !confirme
                ? t('appsMobiles.enCours')
                : t('appsMobiles.enregistrer')}
            </Button>
          </div>
        ) : null}
      </form>

      <Dialog
        open={confirme}
        onOpenChange={(ouvert) => {
          if (!ouvert) setConfirme(false);
        }}
        title={t('appsMobiles.confirmation.titre', {
          plateforme: nom,
          version: reglageDe(brouillon).minimale ?? '',
        })}
        description={t('appsMobiles.confirmation.texte')}
        closeLabel={t('appsMobiles.confirmation.fermer')}
      >
        <div className={formStyles.actionsFormulaire}>
          <Button type="button" variant="secondary" onClick={() => setConfirme(false)}>
            {t('appsMobiles.confirmation.annuler')}
          </Button>
          <Button type="button" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending
              ? t('appsMobiles.enCours')
              : t('appsMobiles.confirmation.confirmer')}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

interface ChampProps {
  id: string;
  champ: ChampVersions;
  valeur: string;
  erreur: 'version' | 'url' | 'conseilleeSousMinimale' | undefined;
  lecture: boolean;
  mono?: boolean;
  large?: boolean;
  placeholder: string;
  onChange: (valeur: string) => void;
}

function Champ({
  id,
  champ,
  valeur,
  erreur,
  lecture,
  mono = false,
  large = false,
  placeholder,
  onChange,
}: ChampProps): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <div className={`${formStyles.champ} ${large ? styles.large : ''}`}>
      <label htmlFor={id}>{t(`appsMobiles.champs.${champ}`)}</label>
      <Input
        id={id}
        mono={mono}
        type={champ === 'urlBoutique' ? 'url' : 'text'}
        inputMode={champ === 'urlBoutique' ? 'url' : 'decimal'}
        autoComplete="off"
        spellCheck={false}
        value={valeur}
        readOnly={lecture}
        placeholder={lecture ? undefined : placeholder}
        aria-invalid={erreur ? true : undefined}
        aria-describedby={erreur ? `${id}-aide ${id}-erreur` : `${id}-aide`}
        onChange={(event) => onChange(event.target.value)}
      />
      <p id={`${id}-aide`} className={formStyles.aide}>
        {t(`appsMobiles.aides.${champ}`)}
      </p>
      {erreur ? (
        <p id={`${id}-erreur`} role="alert" className={formStyles.erreur}>
          {t(`appsMobiles.erreurs.${erreur}`)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Ce que le réglage en cours fait aux apps, en trois plages : bloquées sous la
 * minimale, bandeau jusqu'à la conseillée, admises au-delà. La plage se lit par
 * son texte ; la couleur ne fait que l'appuyer.
 */
function Effet({
  plateforme,
  reglage,
  valide,
}: {
  plateforme: PlateformeApp;
  reglage: ExigencesPlateforme;
  valide: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const { minimale, conseillee } = reglage;
  const plages: Array<{ cle: 'bloquee' | 'bandeau' | 'ok'; texte: string }> = [];

  if (valide && minimale !== null) {
    plages.push({ cle: 'bloquee', texte: t('appsMobiles.rail.bloquee', { version: minimale }) });
  }
  if (
    valide &&
    conseillee !== null &&
    (minimale === null || comparerVersions(minimale, conseillee) < 0)
  ) {
    plages.push({
      cle: 'bandeau',
      texte:
        minimale === null
          ? t('appsMobiles.rail.bandeauSous', { version: conseillee })
          : t('appsMobiles.rail.bandeau', { de: minimale, a: conseillee }),
    });
  }
  const seuil = conseillee ?? minimale;
  if (valide && seuil !== null) {
    plages.push({
      cle: 'ok',
      texte:
        conseillee !== null
          ? t('appsMobiles.rail.aJour', { version: conseillee })
          : t('appsMobiles.rail.admise', { version: seuil }),
    });
  }

  return (
    <div className={styles.effet} data-effet={plateforme}>
      <p className={styles.effetTitre}>{t('appsMobiles.rail.titre')}</p>
      {plages.length === 0 ? (
        <p className={styles.effetVide}>{valide ? t('appsMobiles.rail.aucune') : '—'}</p>
      ) : (
        <ul className={styles.plages}>
          {plages.map((plage) => (
            <li key={plage.cle} className={styles.plage} data-plage={plage.cle}>
              {plage.texte}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
